import React from 'react';
import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Clock, Users, Award, Calendar, Mail, Phone, ArrowRight, Star } from 'lucide-react';
import { Service } from '../../utils/serviceMapper.tsx';

// Re-export Service type for backward compatibility
export type { Service };
interface ServiceDetailProps {
  service: Service;
  onBack?: () => void;
  onGetStartedClick?: () => void;
  onConsultClick?: () => void;
}
export default function ServiceDetail({
  service,
  onBack,
  onGetStartedClick,
  onConsultClick
}: ServiceDetailProps) {
  const [qc, setQc] = useState({ name: '', email: '', phone: '', message: '' });
  const [qcStatus, setQcStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [qcMessage, setQcMessage] = useState('');
  const handleQuickContact = async () => {
    if (!qc.name || !qc.email) {
      setQcStatus('error');
      setQcMessage('Please enter your name and email.');
      return;
    }
    setQcStatus('sending');
    try {
      const res = await fetch('/wp-json/bareeq/v1/lead', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...qc, service: service?.name || '', source: 'service-detail' })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setQcStatus('success');
        setQcMessage('Thank you — our team will contact you within 24 hours.');
        setQc({ name: '', email: '', phone: '', message: '' });
      } else {
        setQcStatus('error');
        setQcMessage(data.message || 'Something went wrong. Please try again.');
      }
    } catch (e) {
      setQcStatus('error');
      setQcMessage('Network error. Please try again.');
    }
  };
  const process = [{
    step: '01',
    title: 'Discovery & Assessment',
    description: 'We analyze your current infrastructure and business requirements'
  }, {
    step: '02',
    title: 'Strategy Development',
    description: 'Custom roadmap designed to meet your specific objectives'
  }, {
    step: '03',
    title: 'Implementation',
    description: 'Expert execution with minimal disruption to operations'
  }, {
    step: '04',
    title: 'Support & Optimization',
    description: 'Ongoing monitoring and continuous improvement'
  }];
  const whyChooseUs = [{
    icon: Award,
    text: '20+ years of industry experience'
  }, {
    icon: Users,
    text: 'Certified IT professionals'
  }, {
    icon: Star,
    text: 'Proven track record in GCC region'
  }, {
    icon: Clock,
    text: '24/7 support and monitoring'
  }];
  return <div className="min-h-screen bg-gray-50">
      {/* Header with Back Button */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button onClick={onBack} className="flex items-center gap-2 text-gray-600 hover:text-[#006847] transition-colors">
            <ArrowLeft size={20} />
            <span className="font-medium">Back to Services</span>
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20 text-white" style={{
      background: `linear-gradient(135deg, ${service.color} 0%, ${service.color}dd 100%)`
    }}>
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} animate={{
            opacity: 1,
            x: 0
          }}>
              <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mb-6">
                {service.icon}
              </div>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                {service.name}
              </h1>
              <p className="text-xl text-white/90 mb-6">
                {service.nameAr}
              </p>
              <p className="text-lg text-white/80 mb-8">
                {service.longDescription}
              </p>
              <button className="bg-white px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 inline-flex items-center gap-2" style={{
              color: service.color
            }} onClick={onGetStartedClick}>
                Get Started
                <ArrowRight size={20} />
              </button>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} className="bg-white/10 backdrop-blur-sm rounded-3xl p-8">
              <h3 className="text-2xl font-bold mb-6">Quick Contact</h3>
              <div className="space-y-4">
                <input type="text" placeholder="Your Name" value={qc.name} onChange={e => setQc({ ...qc, name: e.target.value })} className="w-full px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white" />
                <input type="email" placeholder="Email Address" value={qc.email} onChange={e => setQc({ ...qc, email: e.target.value })} className="w-full px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white" />
                <input type="tel" placeholder="Phone Number" value={qc.phone} onChange={e => setQc({ ...qc, phone: e.target.value })} className="w-full px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white" />
                <textarea placeholder="Tell us about your project..." rows={4} value={qc.message} onChange={e => setQc({ ...qc, message: e.target.value })} className="w-full px-4 py-3 rounded-lg text-gray-800 focus:outline-none focus:ring-2 focus:ring-white resize-none" />
                <button disabled={qcStatus === 'sending'} className="w-full bg-white text-gray-800 py-3 rounded-lg font-semibold hover:bg-gray-100 disabled:opacity-60 transition-colors" onClick={handleQuickContact}>
                  {qcStatus === 'sending' ? 'Sending...' : 'Request Consultation'}
                </button>
                {qcMessage && <p className={`text-sm ${qcStatus === 'success' ? 'text-green-200' : 'text-red-200'}`}>{qcMessage}</p>}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 text-center">
              What We Offer
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Comprehensive solutions designed to address your specific challenges
            </p>

            <div className="grid md:grid-cols-2 gap-6">
              {service.features.map((feature, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="flex items-start gap-4 p-6 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{
                backgroundColor: `${service.color}20`
              }}>
                    <CheckCircle style={{
                  color: service.color
                }} size={24} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-800 mb-1">
                      {feature}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      Professional implementation with industry best practices
                    </p>
                  </div>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 text-center">
              Key Benefits
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              Measurable outcomes that drive business value
            </p>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {service.benefits.map((benefit, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="bg-white p-6 rounded-xl shadow-sm hover:shadow-lg transition-all text-center">
                  <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-4" style={{
                backgroundColor: `${service.color}20`
              }}>
                    <Star style={{
                  color: service.color
                }} size={28} />
                  </div>
                  <h3 className="font-semibold text-gray-800">
                    {benefit}
                  </h3>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }}>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 text-center">
              Our Process
            </h2>
            <p className="text-gray-600 text-center mb-12 max-w-2xl mx-auto">
              A proven methodology that ensures successful outcomes
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {process.map((step, index) => <motion.div key={index} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="relative">
                  <div className="text-center">
                    <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-4 text-2xl font-bold text-white" style={{
                  backgroundColor: service.color
                }}>
                      {step.step}
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {step.description}
                    </p>
                  </div>
                  {index < process.length - 1 && <div className="hidden lg:block absolute top-10 left-full w-full h-0.5 bg-gray-200 -translate-x-1/2" />}
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-12">
              Why Choose Us
            </h2>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {whyChooseUs.map((item, index) => <motion.div key={index} initial={{
              opacity: 0,
              scale: 0.9
            }} whileInView={{
              opacity: 1,
              scale: 1
            }} viewport={{
              once: true
            }} transition={{
              delay: index * 0.1
            }} className="flex flex-col items-center">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mb-4" style={{
                backgroundColor: `${service.color}20`
              }}>
                    <item.icon style={{
                  color: service.color
                }} size={28} />
                  </div>
                  <p className="text-gray-700 font-medium">
                    {item.text}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="rounded-3xl p-12 text-center text-white" style={{
          background: `linear-gradient(135deg, ${service.color} 0%, ${service.color}dd 100%)`
        }}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
              Schedule a free consultation with our experts to discuss your specific needs
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-white px-8 py-4 rounded-full font-semibold transition-all hover:scale-105 inline-flex items-center justify-center gap-2" style={{
              color: service.color
            }} onClick={onConsultClick}>
                <Calendar size={20} />
                Schedule Consultation
              </button>
              <button className="bg-white/10 backdrop-blur-sm border-2 border-white px-8 py-4 rounded-full font-semibold transition-all hover:bg-white/20 inline-flex items-center justify-center gap-2" onClick={onConsultClick}>
                <Phone size={20} />
                Call Us Now
              </button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
}